/* eslint-disable complexity */
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Icon from 'carbon-react/lib/components/icon';

import { ExtendedReportList } from '@futrli/views/processors/reports/reportsList';
import { FormattedMessage } from '@futrli/localisation';
import { getReportingEditReportUrl, matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { ListingPageRowActionItem } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-actions/listing-page-row-actions';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

import { usePredictFlags } from '../../../../../hooks/usePredictFlags';

interface UseReportActionItemsParams {
  report: ExtendedReportList;
  onLockUnlock?: () => void;
  onPublishUnpublish?: () => void;
  onDelete?: () => void;
}

export const useReportActionItems = ({
  report,
  onLockUnlock,
  onPublishUnpublish,
  onDelete
}: UseReportActionItemsParams) => {
  const navigate = useNavigate();
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);
  const { hasViewDownloadPdfOptionsEnabled } = usePredictFlags();
  const {
    duplicateReport,
    exportReport,
    emailPdfReport,
    template,
    scheduleReport,
    shareResource,
    viewPdfReport
  } = useSidebarContext();

  const items = useMemo<ListingPageRowActionItem[]>(() => {
    const result: ListingPageRowActionItem[] = [];
    const { actions } = report;

    if (actions.edit) {
      result.push({
        label: <FormattedMessage id="common.edit" defaultMessage="Edit" />,
        onClick: () => navigate(getReportingEditReportUrl(workspaceId, organisationId, report.id, isConsolidation))
      });
    }

    if (actions.duplicate) {
      result.push({
        label: <FormattedMessage id="common.duplicate" defaultMessage="Duplicate" />,
        onClick: () => {
          duplicateReport.open(report);
        }
      });
    }

    if (actions.publish) {
      result.push({
        label: <FormattedMessage id="common.publish" defaultMessage="Publish" />,
        onClick: () => {
          onPublishUnpublish?.();
        }
      });
    }

    if (actions.share) {
      result.push({
        label: <FormattedMessage id="common.manage_access" defaultMessage="Manage access" />,
        onClick: () => {
          shareResource.open({
            entityId: report.id,
            entityType: 'live-report',
            resourceName: report.name
          });
        }
      });
    }

    if (actions.lock) {
      result.push({
        label: <FormattedMessage id="reporting.lock" defaultMessage="Lock" />,
        onClick: () => {
          onLockUnlock?.();
        }
      });
    }

    if (actions.unpublish) {
      result.push({
        label: <FormattedMessage id="common.unpublish" defaultMessage="Unpublish" />,
        onClick: () => {
          onPublishUnpublish?.();
        }
      });
    }

    if (actions.unlock) {
      result.push({
        label: <FormattedMessage id="reporting.unlock" defaultMessage="Unlock" />,
        onClick: () => {
          onLockUnlock?.();
        }
      });
    }

    if (actions.createSchedule) {
      result.push({
        label: <FormattedMessage id="predict.actions.create_schedule" defaultMessage="Create schedule" />,
        onClick: () => {
          scheduleReport.open({ reportId: report.id, reportName: report.name });
        }
      });
    }

    if (actions.emailPdf) {
      result.push({
        label: <FormattedMessage id="predict.actions.email_pdf" defaultMessage="Email PDF report" />,
        onClick: () => {
          emailPdfReport.open({ reportId: report.id, reportName: report.name });
        }
      });
    }

    if (actions.exportPdf && !hasViewDownloadPdfOptionsEnabled) {
      result.push({
        label: <FormattedMessage id="predict.actions.export_pdf" defaultMessage="Export to PDF" />,
        onClick: () => {
          exportReport.open(report);
        }
      });
    }

    if (actions.viewPdf && hasViewDownloadPdfOptionsEnabled) {
      result.push({
        label: <FormattedMessage id="predict.actions.download_pdf" defaultMessage="Download PDF" />,
        onClick: () => {
          viewPdfReport.open({ report });
        }
      });
    }

    if (actions.addToTemplates) {
      result.push({
        label: <FormattedMessage id="predict.actions.add_to_templates" defaultMessage="Add to templates" />,
        onClick: () => {
          template.open({ reportId: report.id });
        }
      });
    }

    if (actions.delete) {
      result.push({
        label: (
          <>
            <Icon type="delete" fontSize="small" />
            <FormattedMessage id="common.delete" defaultMessage="Delete" />
          </>
        ),
        onClick: () => {
          onDelete?.();
        },
        variant: 'danger'
      });
    }

    return result;
  }, [
    report,
    navigate,
    workspaceId,
    organisationId,
    isConsolidation,
    duplicateReport,
    exportReport,
    emailPdfReport,
    template,
    scheduleReport,
    shareResource,
    onLockUnlock,
    onPublishUnpublish,
    onDelete,
    viewPdfReport,
    hasViewDownloadPdfOptionsEnabled
  ]);

  return items;
};
